import React, { memo, useCallback, useEffect, useState } from 'react';
import { ArrayTwoOrMore, Channel, ChannelFilters, ChannelSort } from 'stream-chat';
import { ChatListViewHeader } from './ChatListViewHeader';
import { ChannelList, InfiniteScroll } from 'stream-chat-react';
import { useAuth } from '../../../hooks/useAuth';
import { ChatFilter } from '../../../models/ChatFilter';
import { ChatListTile } from './ChatListTile';
import { checkAndUpdateMerchantCountIfNeeded, createChatChannelModel } from '../../../utils/ChatChannelUtils';
import Loader from './Loader';
import { useRouter } from '../../../hooks/useRouter';
import { useTextSearch } from '../../../hooks/useTextSearch';
import { ChatChannelType } from '../../../models/ChatChannel';
import { Button, ButtonSize } from '../../../components/Button';
import { ButtonVariant } from '../../../config/colors';
import { useTheme } from '../../../hooks/useTheme';
import { AuthenticationStatusGetStream } from '../../../store/slices/authSlice';
import { TitleBar } from '../../../components/TitleBar';
import { useStream } from '../../../hooks/useStream';
import { FriendshipStatusType } from '../../../models/FriendshipStatus';
import { ConnectionTypes } from '../../../models/Connection';
import { connectionRepository } from '../../../repositories/ConnectionRepository';
import { filtersUnreadStore } from './filtersUnreadStore';
import { ChatChannelUser } from '../../../models/ChatChannelUser';
import { useLocation } from 'react-router-dom';

interface ChatListViewProps {
  
}

interface CustomChannelFilters extends ChannelFilters {
  status?: { $eq: string };
  merchantCount?: { $eq: number };
  has_unread?: { $eq: boolean };
}

const ChatListView: React.FC<ChatListViewProps> = () => {
  const [selectedFilter, setSelectedFilter] = useState(ChatFilter.PERSONAL);
  const [unreadOnlyFilter, setUnreadOnlyFilter] = useState<boolean>(false);
  const {pleb} = useAuth();
  const {statusGetStream, nbUnread} = useStream();
  const {navigateFromChannel, navigateToProfile} = useRouter();
  const { textSearch } = useTextSearch();
  const location = useLocation();
  const [nbRefresh, setNbRefresh] = useState(0);
  const {isMobile} = useTheme();

  useEffect(() => {
    if(location.pathname === '/home/chat') {
      setUnreadOnlyFilter(false);
    }
  }, [location.pathname]);

  const shouldHideWaitingForReply = () => {
    return pleb?.id === '634450c45a1df87a781ef269' || pleb?.id === '66ba8c56a2c151a6187fc6a2' || pleb?.id === '63ffb2c49dd930837385fdd8';
  }

  const getFiltersFromFilter = (filter: ChatFilter | null, textSearch: string) => {
    const filtersOr = [];

    //return {
    //  members: { $in: [pleb?.id || ''] },
    //};

    const filters: CustomChannelFilters = {
      members: { $in: [pleb?.id || '', nbRefresh.toString()] },
      type: { $in : [ChatChannelType.Messaging, ChatChannelType.Event, ChatChannelType.Group]},
      disabled: {$eq: false}
    };
  
    if (textSearch !== '') {
      const queryFilters : ArrayTwoOrMore<ChannelFilters> = [
        { 
          $and: [
            { "member.user.name": { $autocomplete: textSearch } },
            { "member_count": { $lte: 2 } },
          ],
        },
        { 
          $and: [
            { "name": { $autocomplete: textSearch } },
            { "member_count": { $gte: 2 } },
          ],
        },
      ];

      filtersOr.push(queryFilters)
    }
  
    switch(filter) {
      case ChatFilter.GROUPS:
        filters.type = { $eq: filter };
        break;
      case ChatFilter.PERSONAL:
        filters.type = { $eq: ChatChannelType.Messaging };
        break;
      case ChatFilter.EVENTS:
        filters.type = { $eq: filter };
        break;
      case ChatFilter.MERCHANTS:
        filters.merchantCount = { $eq: pleb?.merchant ? 2 : 1 };
        break;
    }

    if (filtersOr.length > 0) {
      if(filtersOr.length === 1)
        filters["$or"] = filtersOr[0];
      else
      {
        // @ts-ignore
        filters["$and"] = filtersOr.map((data) => {
          return {
            "$or": data
          }
        });
      }
    }

    return filters;
  }

  const options = { 
    //presence: true, 
    state: true, 
    watch: true,
    limit: 20
  };

  const getSortFromFilter = (filter: ChatFilter | null): ChannelSort => {
    if(filter === ChatFilter.PERSONAL && shouldHideWaitingForReply()) {
      // TEMPORARY FIX to fix getstream bug with sorting
      return [
        { 
          last_updated: -1 
        }
      ];
    }

    return [
      { 
        pinned_at: -1,
        last_updated: -1 
      }
    ];
  }

  const PreviewWrapper = (filter: ChatFilter | null, props: any, unreadOnlyFilter: boolean = false) => {
    const chatChannel = createChatChannelModel(props.channel, pleb?.id || '');

    if(filter === ChatFilter.PERSONAL) {
      if(chatChannel.type != ChatChannelType.Messaging)
        return null;
      else if(chatChannel instanceof ChatChannelUser && chatChannel.merchant === true)
        return null;
    } else if(filter === ChatFilter.EVENTS) {
      if(chatChannel.type != ChatChannelType.Event)
        return null;
    } else if(filter === ChatFilter.GROUPS) {
      if(chatChannel.type != ChatChannelType.Group)
        return null;
    } else if(filter === ChatFilter.MERCHANTS) {
      if(chatChannel.type != ChatChannelType.Messaging)
        return null;
      else if(chatChannel instanceof ChatChannelUser && chatChannel.merchant === false)
        return null;
    }

    if(chatChannel.type === ChatChannelType.Messaging && chatChannel?.banned === true) {
      return null;
    }

    if(unreadOnlyFilter === true && chatChannel.unreadCount <= 0) {
      return null;
    }

    if(
      shouldHideWaitingForReply() && 
      chatChannel.type === ChatChannelType.Messaging && 
      chatChannel.friendshipStatus.type === FriendshipStatusType.RequestSent
    ) {
      return null;
    }

    // Defer unread updates to an effect in a child component to avoid updates during render
    const PreviewInner: React.FC = () => {
      useEffect(() => {
        if(filter !== null) {
          filtersUnreadStore.updateUnread(filter, chatChannel.id, chatChannel.unreadCount > 0);
        }
      }, [filter, chatChannel.id, chatChannel.unreadCount]);

      useEffect(() => {
        if(
          chatChannel.friendshipStatus.type === FriendshipStatusType.RequestReceived && 
          chatChannel.type === ChatChannelType.Messaging &&
          chatChannel.lastMessage?.isCurrentUser === true && 
          chatChannel.channel.data.connectionId
        ) {
          connectionRepository.updateConnection(
            chatChannel.channel.data.connectionId,
            ConnectionTypes.ESTABLISHED
          );
        }
      }, [chatChannel.friendshipStatus.type, chatChannel.type, chatChannel.lastMessage?.isCurrentUser, chatChannel.channel.data.connectionId]);

      return <ChatListTile 
        chatChannel={chatChannel}
        onTap={() => {
          if(chatChannel.friendshipStatus.type === FriendshipStatusType.RequestSent && chatChannel.friendshipStatus.isBlocked) {
            navigateToProfile(chatChannel.type ?? ChatChannelType.Messaging, chatChannel.id);
          } else {
            navigateFromChannel(props.channel);
          }
        }}
        isSelected={props.activeChannel?.id === props.channel.id}
      />
    };

    return <PreviewInner />
  };

  const renderLoadingErrorIndicator = (withRetry: boolean = true) => {
    return <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
      <div className="flex flex-col items-center justify-center">
        <p className="text-center text-base opacity-50">
          An error occured while loading the channels
        </p>
        {withRetry && <Button
          variant={ButtonVariant.primary}
          onClick={() => setNbRefresh(nbRefresh + 1)}
          size={ButtonSize.medium}
          className="mt-4"
        >
            <i className='material-icons'>refresh</i> Retry
          </Button>
        }
      </div>
    </div>
  }

  const renderChannels = (loadedChannels: Channel[], channelPreview: (item: Channel) => React.ReactNode) => {
    checkAndUpdateMerchantCountIfNeeded(loadedChannels)
    return removeDuplicatesById(loadedChannels)
      .map((channel) => channelPreview(channel))
  }

  const renderChannelList = useCallback((filter: ChatFilter | null, textSearch: string = '', unreadOnlyFilter: boolean = false, setUnreadOnlyFilter: (unreadOnlyFilter: boolean) => void) => {
    if(statusGetStream === AuthenticationStatusGetStream.LOADING) return <Loader />;
    if(statusGetStream === AuthenticationStatusGetStream.ERROR) return renderLoadingErrorIndicator(false);
    if(statusGetStream === AuthenticationStatusGetStream.UNAUTHENTICATED) return <Loader />;

    const filtersLocal = getFiltersFromFilter(filter, textSearch);

    if(!filtersLocal) return null;

    return <ChannelList 
      filters={filtersLocal} 
      options={options} 
      sort={getSortFromFilter(filter)} 
      Preview={(props: any) => PreviewWrapper(filter, props, unreadOnlyFilter)}
      EmptyStateIndicator={() => renderEmptyState(filter, textSearch, isMobile, unreadOnlyFilter, setUnreadOnlyFilter)}
      LoadingIndicator={() => <Loader />}
      setActiveChannelOnMount={false}
      renderChannels={renderChannels}
      LoadingErrorIndicator={() => renderLoadingErrorIndicator(true)}
      Paginator={InfiniteScroll}
      channelRenderFilterFn={unreadOnlyFilter ? (channels) => channels.filter((channel) => (channel.state?.unreadCount ?? 0) > 0) : undefined}
    />
  }, [statusGetStream, isMobile, textSearch, pleb?.id, nbRefresh]);

  return (
    <div className="flex-1 pt-8 overflow-hidden flex flex-col gap-8">
      <TitleBar className="px-4" title="Chat" badgeMobile={nbUnread} />

      <div className="flex flex-col overflow-hidden">
        <ChatListViewHeader 
          selectedFilter={selectedFilter} 
          handleFilterChange={(filter) => {
            setSelectedFilter(filter);
            setUnreadOnlyFilter(false);
          }}
          handleFilterDoubleClick={(filter) => {
            setSelectedFilter(filter);
            setUnreadOnlyFilter((prevFilter) => !prevFilter);
          }}
        />
        {
          textSearch !== '' && (
            <div className="flex-1 overflow-hidden">
              {renderChannelList(null, textSearch, unreadOnlyFilter, setUnreadOnlyFilter)}
            </div>
          )
        }
        {
          Object.values(ChatFilter).map((filter) => (
            <div key={filter} className={`flex-1 overflow-hidden ${filter === selectedFilter && textSearch === '' ? '' : 'hidden'}`}>
              {renderChannelList(filter, undefined, unreadOnlyFilter, setUnreadOnlyFilter)}
            </div>
          ))
        }
      </div>
    </div>
  );
};

export default memo(ChatListView);

const removeDuplicatesById = (arr: any[]) => {
  const seenIds = new Set();
  return arr.filter((obj: any) => !seenIds.has(obj.id) && seenIds.add(obj.id));
};

const renderUnreadOnlyEmptyState = (isMobile: boolean, onViewAllMessages: () => void) => {
  return (
    <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
      <p className="text-center text-base opacity-50">
        No Unread Messages
      </p>
      <button
        type="button"
        className="mt-3 text-brand-orange underline"
        onClick={onViewAllMessages}
      >
        View All Messages
      </button>
    </div>
  );
}

const renderEmptyState = (
  selectedFilter: ChatFilter | null, 
  textSearch: string | null, 
  isMobile: boolean, 
  unreadOnlyFilter: boolean = false,
  setUnreadOnlyFilter: (unreadOnlyFilter: boolean) => void
) => { 
  if(unreadOnlyFilter) {
    return renderUnreadOnlyEmptyState(isMobile, () => setUnreadOnlyFilter(false));
  }

  let message = `Nothing to see here yet, connect with some bitcoiners and when they reply their messages will appear here`;

  switch(selectedFilter)
  {
    case ChatFilter.PERSONAL:
      message = `Nothing to see here yet, connect with some bitcoiners and when they reply their messages will appear here`;
      break;
    case ChatFilter.MERCHANTS:
      message = "Nothing to see here yet, connect with some merchants and when they reply their messages will appear here"
      break;
    case ChatFilter.EVENTS:
      message = "Nothing to see here yet, when you join an event group chat your messages will appear here"
      break;
    case ChatFilter.GROUPS:
      message = "Nothing to see here yet, when you join a group chat your messages will appear here"
      break;
  }

  if(textSearch !== '')
    message = "No results, try to search something else";

  return (
    <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
      <p className="text-center text-base opacity-50">
        {message}
      </p>
    </div>
  )
}